import { useState } from 'react'
import type { Job } from '@workflow/contracts'
import { Avatar, AvatarStack, Icons, ProgressRing, cn } from '@workflow/ui'
import { useMembers, useSession, useStudioHours } from '@/lib/api/hooks'
import { personName } from '@/lib/people'
import { hoursFigure } from '@/features/jobs/jobsStatus'
import { MonthSwitcher } from '@/features/jobs/MonthSwitcher'
import { monthKey, monthName } from '@/features/jobs/jobPeriods'
import { Card, WeekBars, changeText, monthWeeks, previousMonth } from '@/features/overview/OverviewParts'
import { Section } from '@/shell/PageFrame'

/** Why a job asks for the studio's eye, most pressing first. */
export type AttentionReason = 'ready' | 'handed_short' | 'no_crew' | 'short' | 'paused' | 'no_rate'

export const ATTENTION_LABEL: Record<AttentionReason, string> = {
  ready: 'Ready To Close',
  handed_short: 'Handed In Short',
  no_crew: 'No Crew',
  short: 'Needs Players',
  paused: 'Paused',
  no_rate: 'No Rate',
}

const LIVE: readonly Job['status'][] = ['published', 'in_progress', 'on_hold']
const isLive = (job: Job) => job.archivedAt === null && LIVE.includes(job.status)

/**
 * The first reason a live job needs someone, or null. Every crew member
 * done and the job still open comes first — it is waiting only on the
 * studio — then work nobody holds, work with fewer hands than it asks for,
 * a pause, and work with no player rate to pay it by.
 */
export function attentionOf(job: Job): AttentionReason | null {
  if (!isLive(job)) return null
  if (job.readyToComplete) return 'ready'
  if (job.handedInShort) return 'handed_short'
  if (job.status !== 'on_hold' && job.crew.length === 0) return 'no_crew'
  // The crew already counts only the hands on it or finished; dropped and removed ones left it.
  if (job.status !== 'on_hold' && job.playersNeeded !== null && job.crew.length < job.playersNeeded) return 'short'
  if (job.status === 'on_hold') return 'paused'
  if (job.playerRateAmount === null) return 'no_rate'
  return null
}

export interface StudioNow {
  running: Job[]
  /** Hours logged and planned across the running work, for its ring. */
  runningLogged: number
  runningPlanned: number
  waiting: Job[]
  waitingWithoutCrew: number
  attention: { job: Job; reason: AttentionReason }[]
}

const ORDER: AttentionReason[] = ['ready', 'handed_short', 'no_crew', 'short', 'paused', 'no_rate']

/** What the studio's work looks like right now, from the jobs it already holds. */
export function studioNow(jobs: readonly Job[]): StudioNow {
  const running = jobs.filter((job) => job.archivedAt === null && job.status === 'in_progress')
  const planned = running.filter((job) => (job.estimatedHours ?? 0) > 0)
  const waiting = jobs.filter((job) => job.archivedAt === null && job.status === 'published')
  const attention = jobs
    .map((job) => ({ job, reason: attentionOf(job) }))
    .filter((entry): entry is { job: Job; reason: AttentionReason } => entry.reason !== null)
    .sort((a, b) => ORDER.indexOf(a.reason) - ORDER.indexOf(b.reason))
  return {
    running,
    runningLogged: planned.reduce((total, job) => total + job.loggedHours, 0),
    runningPlanned: planned.reduce((total, job) => total + (job.estimatedHours ?? 0), 0),
    waiting,
    waitingWithoutCrew: waiting.filter((job) => job.crew.length === 0).length,
    attention,
  }
}

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`

/**
 * The main page's overview — the player's Home, turned to face the studio:
 * the same soft cards, but about now rather than about one person. The
 * month's logged hours with their weeks, what is running and how far along,
 * what is waiting to begin, what needs a hand, and who is on the clock.
 * Finished work stays on the Jobs page; this is the work still moving.
 */
export function StudioOverview({ jobs, onOpenJob }: { jobs: readonly Job[]; onOpenJob: (job: Job) => void }) {
  const [month, setMonth] = useState(() => monthKey(new Date()))
  const hours = useStudioHours(month)
  const isAdmin = useSession().data?.account?.role === 'admin'
  const members = useMembers(isAdmin)
  const now = studioNow(jobs)

  // While a new month loads, the last one stays on the cards rather than a flash of zeros.
  const days = hours.data?.days ?? []
  const total = days.reduce((sum, day) => sum + day.hours, 0)
  const weeks = monthWeeks(month, days.map((day) => ({ day: Number(day.day.slice(8, 10)), hours: day.hours })))
  const before = monthName(previousMonth(month)).split(' ')[0]!
  const clocked = (members.data ?? []).filter(
    (member) => member.role === 'player' && member.status === 'active' && member.availability === 'active',
  )

  return (
    <Section title="Overview" aside={<MonthSwitcher value={month} onChange={setMonth} />}>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Card className="col-span-2 sm:row-span-2">
          <p className="text-caption text-muted-foreground">Hours Logged</p>
          <p className="mt-1 flex items-baseline gap-1.5">
            <span className="font-mono text-5xl leading-none tabular-nums text-foreground">{hoursFigure(total)}</span>
            <span className="text-ui text-muted-foreground">Hours</span>
          </p>
          <p className="mt-2 text-caption text-muted-foreground">
            {hours.isLoading ? 'Loading…' : changeText(total, hours.data?.previousHours ?? 0, before, 'No Hours Logged Yet')}
          </p>
          <WeekBars weeks={weeks} />
        </Card>

        <Card>
          <div className="flex items-start justify-between gap-2">
            <p className="text-caption text-muted-foreground">Running Now</p>
            {now.runningPlanned > 0 ? (
              <ProgressRing value={now.runningLogged / now.runningPlanned} size={28} label="Hours logged against hours planned on running work" />
            ) : null}
          </div>
          <p className="mt-3 font-mono text-3xl leading-none tabular-nums text-foreground">{now.running.length}</p>
          <p className="mt-2 text-caption text-muted-foreground">
            {now.running.length === 0
              ? 'Nothing Running'
              : now.runningPlanned > 0
                ? `${hoursFigure(now.runningLogged)} Of ${hoursFigure(now.runningPlanned)} Hours Logged`
                : plural(now.running.length, 'Job In Progress', 'Jobs In Progress')}
          </p>
        </Card>

        <Card>
          <div className="flex items-start justify-between gap-2">
            <p className="text-caption text-muted-foreground">Not Started</p>
            <span className="text-muted-foreground" aria-hidden><Icons.time size="sm" /></span>
          </div>
          <p className="mt-3 font-mono text-3xl leading-none tabular-nums text-foreground">{now.waiting.length}</p>
          <p className="mt-2 text-caption text-muted-foreground">
            {now.waiting.length === 0
              ? 'Nothing Waiting'
              : now.waitingWithoutCrew > 0
                ? `${now.waitingWithoutCrew} Without Crew`
                : 'All Have Crew'}
          </p>
        </Card>

        <Card className={cn('col-span-2', !isAdmin && 'sm:col-span-3')}>
          <div className="flex items-start justify-between gap-2">
            <p className="text-caption text-muted-foreground">Needs Attention</p>
            <span className="font-mono text-ui tabular-nums text-foreground">{now.attention.length}</span>
          </div>
          {now.attention.length === 0 ? (
            <p className="mt-3 text-ui text-muted-foreground">All Clear — Nothing Needs You Right Now</p>
          ) : (
            <ul className="-mx-2 mt-2 flex flex-col">
              {now.attention.slice(0, 4).map(({ job, reason }) => (
                <li key={job.id}>
                  <button
                    type="button"
                    onClick={() => onOpenJob(job)}
                    className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left outline-none transition-colors duration-fast hover:ring-1 hover:ring-inset hover:ring-foreground/20 focus-visible:ring-1 focus-visible:ring-foreground"
                  >
                    <span className="min-w-0 flex-1 truncate text-ui text-foreground">{job.title}</span>
                    <span className="shrink-0 whitespace-nowrap rounded-full bg-foreground px-2 py-0.5 text-micro text-background">{ATTENTION_LABEL[reason]}</span>
                  </button>
                </li>
              ))}
              {now.attention.length > 4 ? (
                <li className="px-2 pt-1 text-caption text-muted-foreground">{`And ${now.attention.length - 4} More`}</li>
              ) : null}
            </ul>
          )}
        </Card>

        {isAdmin ? (
          <Card className="col-span-2 sm:col-span-1">
            <div className="flex items-start justify-between gap-2">
              <p className="text-caption text-muted-foreground">Clocked In</p>
              <span className="size-2 rounded-full bg-status-green" aria-hidden />
            </div>
            <p className="mt-3 font-mono text-3xl leading-none tabular-nums text-foreground">{clocked.length}</p>
            {clocked.length === 0 ? (
              <p className="mt-2 text-caption text-muted-foreground">Nobody On The Clock</p>
            ) : clocked.length <= 3 ? (
              <ul className="mt-3 flex flex-col gap-2">
                {clocked.map((member) => (
                  <li key={member.id} className="flex min-w-0 items-center gap-2">
                    <Avatar name={personName(member)} email={member.email} src={member.pictureUrl} size="xs" />
                    <span className="truncate text-caption text-foreground">{personName(member)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="mt-3 flex items-center gap-2">
                <AvatarStack
                  people={clocked.map((member) => ({ key: member.id, name: personName(member), email: member.email, src: member.pictureUrl }))}
                 
                />
                <span className="truncate text-caption text-muted-foreground">{plural(clocked.length, 'Player', 'Players')}</span>
              </div>
            )}
          </Card>
        ) : null}
      </div>
    </Section>
  )
}
